/**
 * ImpersonateUserDialog — admin "Impersonate User" picker.
 *
 * A ContinuumAdmin picks a user; we call auth.ImpersonateUser, back up the
 * admin's token, and hand off to the impersonated session (the whole app then
 * acts as that user). Mirrors JobAccessDialog's imperative trigger + host
 * pattern (Subject -> always-mounted Host owns the Dialog).
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { UserGroupRow, listUserGroups } from "../../services/admin-rpc-client";
import { beginImpersonation } from "../../services/impersonation";
import { errorMessage } from "../../utils/error-message";
import { showImpersonateDialog } from "./impersonate-signal";

export const ImpersonateUserDialogHost: React.FC = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const sub = showImpersonateDialog.subscribe((next) => setOpen(next));
    return () => sub.unsubscribe();
  }, []);
  return <ImpersonateUserDialog open={open} onClose={() => showImpersonateDialog.next(false)} />;
};

interface Props {
  open: boolean;
  onClose: () => void;
}

const ImpersonateUserDialog: React.FC<Props> = ({ open, onClose }) => {
  const [users, setUsers] = useState<UserGroupRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [target, setTarget] = useState<UserGroupRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setError(null);
    setLoading(true);
    setFetchError(null);
    // User-shaped identities only (show_as_user) — excludes the role-group rows.
    // Workers (e.g. dev@a8-apps) ARE included: the ActivatedUser WS gate now lets
    // a worker identity open a browser session, so impersonating one works.
    listUserGroups()
      .then((rows) => setUsers(rows.filter((r) => r.show_as_user)))
      .catch((e: unknown) => setFetchError(errorMessage(e) || String(e)))
      .finally(() => setLoading(false));
  }, [open]);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.name.localeCompare(b.name)),
    [users],
  );

  const handleImpersonate = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      // Navigates on success (handoff + reload) — no further code runs here.
      await beginImpersonation(target.uid);
    } catch (e) {
      setError(errorMessage(e) || String(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Impersonate user</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          View the app as another user. You stay signed in as yourself underneath
          — a banner will let you return to admin at any time.
        </DialogContentText>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {fetchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fetchError}
          </Alert>
        )}
        {!loading && (
          <Autocomplete
            size="small"
            options={sortedUsers}
            getOptionLabel={(u) => (u.email ? `${u.name} <${u.email}>` : u.name)}
            isOptionEqualToValue={(a, b) => a.uid === b.uid}
            value={target}
            onChange={(_e, val) => setTarget(val)}
            renderInput={(params) => (
              <TextField {...params} label="User to impersonate" autoFocus />
            )}
          />
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!target || busy}
          onClick={handleImpersonate}
        >
          {busy ? <CircularProgress size={18} /> : "Impersonate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImpersonateUserDialog;
